// src/components/EcoScoreBoard/ecoScoreCalculator.js

const calculateGreenSpaceScore = (elements, totalArea) => {
    const greenElements = elements.filter(el => 
      ['Park', 'Urban Forest', 'Green Belt', 'Eco-Pond'].includes(el.type)
    );
    
    const greenArea = greenElements.reduce((sum, el) => sum + (el.width * el.height), 0);
    const greenRatio = greenArea / totalArea;
    
    // Aim for 30% green space as optimal
    return Math.min(100, (greenRatio * 300));
  };
  
  const calculateWaterScore = (elements, totalArea) => {
    const waterFeatures = elements.filter(el => 
      el.properties.hasWaterFeature || 
      ['Eco-Pond', 'Community Lake'].includes(el.type)
    );
    
    const waterArea = waterFeatures.reduce((sum, el) => sum + (el.width * el.height), 0);
    const waterRatio = waterArea / totalArea;
    
    // Aim for 10% water features as optimal
    const areaScore = Math.min(70, (waterRatio * 700));
    
    // Additional points for water management features
    const managementScore = elements.reduce((score, el) => {
      if (el.properties.hasRainwaterHarvesting) score += 10;
      if (el.properties.hasPermablePaving) score += 10;
      if (el.properties.hasWaterRecycling) score += 10;
      return score;
    }, 0);
    
    return Math.min(100, areaScore + managementScore);
  };
  
  const calculateSustainableDesignScore = (elements) => {
    const buildings = elements.filter(el => el.category === 'building');
    if (!buildings.length) return 0;
    
    return buildings.reduce((score, building) => {
      if (building.properties.hasGreenRoof) score += 25;
      if (building.properties.hasSolarPanels) score += 25;
      if (building.properties.hasEnergyEfficiency) score += 25;
      if (building.properties.requiresGreenSpace) score += 25;
      return score;
    }, 0) / buildings.length;
  };
  
  const calculateBiodiversityScore = (elements) => {
    let score = 0;
    
    // Score for variety of green spaces
    const uniqueGreenSpaces = new Set(
      elements
        .filter(el => el.category === 'garden')
        .map(el => el.type)
    ).size;
    score += uniqueGreenSpaces * 15;
    
    // Score for biodiversity features
    elements.forEach(el => {
      if (el.properties.supportsBiodiversity) score += 20;
      if (el.properties.hasWildlife) score += 20;
      if (el.properties.hasNativeSpecies) score += 20;
    });
    
    // Score for ecological connectivity
    const hasEcologicalCorridor = elements.some(el => 
      el.type === 'Green Belt' && 
      elements.some(other => 
        other.type === 'Urban Forest' && 
        Math.abs(other.x - el.x) < 100 && 
        Math.abs(other.y - el.y) < 100
      )
    );
    if (hasEcologicalCorridor) score += 20;
    
    return Math.min(100, score);
  };
  
  export const calculateEcoScores = (elements) => {
    if (!elements?.length) {
      return {
        greenSpace: 0,
        waterManagement: 0,
        sustainableDesign: 0,
        biodiversity: 0,
        overall: 0
      };
    }
  
    // Calculate total area
    const totalArea = elements.reduce((sum, el) => sum + (el.width * el.height), 0);
    
    // Calculate individual scores
    const scores = {
      greenSpace: calculateGreenSpaceScore(elements, totalArea),
      waterManagement: calculateWaterScore(elements, totalArea),
      sustainableDesign: calculateSustainableDesignScore(elements),
      biodiversity: calculateBiodiversityScore(elements)
    };
    
    // Calculate overall score with weighted average
    scores.overall = Math.round(
      (scores.greenSpace * 0.3) +
      (scores.waterManagement * 0.2) +
      (scores.sustainableDesign * 0.25) +
      (scores.biodiversity * 0.25)
    );
    
    return scores;
  };